import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { CommentsDataService, CommentsColumnsService } from '../../services';
import { DialogCommentFormComponent } from './comment-form/comment-form.component';
import { DialogCommentDetailsComponent } from './comment-details/comment-details.component';
import { Comment, CommentsResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-comments',
  templateUrl: './comments.component.html',
  styleUrls: ['./comments.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class CommentsComponent implements OnInit, OnDestroy {

  private commentAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private commentEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private commentDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public commentToDelete: Comment = {};
  public comments: Comment[] = [];
  public comment: Comment = {};
  public selectedComments: Comment[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private commentsDataService: CommentsDataService,
    private commentsColumnsService: CommentsColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('CommentsComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Comments - Management');
    this.primengConfig.ripple = true;
    this.getComments();
  }

  ngOnDestroy(): void {
    console.log('CommentsComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getComments(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.commentsDataService.getComments(this.parameters).subscribe((response: CommentsResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.comments = response.results;
        } else {
          this.comments = this.comments.concat(response.results);
        }
        if (response.inlineCount > this.comments.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getComments();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('COMMENTSNOTLOADED')});
    });
  }

  importComments(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.commentsDataService.addComments(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('COMMENTSCREATED')});
                if (proceed === j) {
                  this.getComments();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('COMMENTSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('COMMENTSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.commentsDataService.updateComments(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('COMMENTSUPDATED')});
                if (proceed === j) {
                  this.getComments();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('COMMENTSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('COMMENTSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.commentsColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('comments.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedComments() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Comments?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.commentsDataService.deleteComments(this.selectedComments).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Comments Deleted', life: 3000});
            this.getComments();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Comments not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Comments not deleted'});
        });
        this.selectedComments = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.comments, this.columns, 'Comments');
  }

  exportExcel() {
    ToolsService.exportExcel(this.comments, this.columns, 'Comments');
  }

  exportCsv() {
    ToolsService.exportCsv(this.comments, this.columns, 'Comments');
  }

  refresh() {
    this.commentsDataService.clearCache();
    this.getComments();
  }

  commentAdd(): void {
    if (!this.navigation.getParamValue('comments.addInDialog', true)) {
      this.router.navigate(['/comments/add']);
      return;
    }
    this.commentAddDialog = this.dialogService.open(DialogCommentFormComponent, {
      header: '', // this.translate.instant('NEWCOMMENT'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        comment: {id: null},
        cities: this.commentsColumnsService.cities,
        users: this.commentsColumnsService.users,
      },
    });

    this.commentAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'Comment added'});
        this.getComments();
      }
    });
  }

  commentEdit(comment: Comment): void {
    if (!this.navigation.getParamValue('comments.editInDialog', true)) {
      this.router.navigate(['/comments/edit/' + comment.id]);
      return;
    }
    this.commentEditDialog = this.dialogService.open(DialogCommentFormComponent, {
      header: '', // this.translate.instant('EDITCOMMENT') + ' ' + comment.entity,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        comment: comment,
        cities: this.commentsColumnsService.cities,
        users: this.commentsColumnsService.users,
      },
    });

    this.commentEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'Comment updated'});
        this.getComments();
      }
    });
  }

  commentDetails(comment: Comment): void {
    if (!this.navigation.getParamValue('comments.detailsInDialog', true)) {
      this.router.navigate(['/comments/details/' + comment.id]);
      return;
    }
    this.commentDetailsDialog = this.dialogService.open(DialogCommentDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        comment: comment,
      }
    });
  }

  commentDelete(comment: Comment): void {
    this.commentToDelete = comment;
    this.messageService.clear();
    this.messageService.add({
      key: 'commentDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + comment.entity + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.commentsDataService.deleteComment(this.commentToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Comment deleted'});
      this.getComments();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Comment not deleted'});
    });
  }

  onDeleteReject(): void {
    this.commentToDelete = {};
    this.messageService.clear('commentDelete');
  }

}
